import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isSubset } from 'es-toolkit'
import { useRxCollection } from 'rxdb-hooks'
import { toast } from 'ui/sonner/sonner.js'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { getPlayableCardsSnoozeSelector } from '~/boundedContext/cards/utils/card'
import type { Card } from '~/db/types'
import { useLastRevertedCardId } from '~/hooks/use-card-deletion-revert'
import { useAccountStore } from '~/stores/account'
import { useCardsGameStore } from '~/stores/cards-game'
import { useSettingsStore } from '~/stores/settings'

// TODO: find a better way to store this state
const usePrevAllNonSnoozedCardsIds = create<{ value: string[] }>()(
  persist(() => ({ value: [] as string[] }), {
    name: 'prev-all-non-snoozed-cards-ids',
  }),
)

export const useStackRefreshToast = () => {
  const { t } = useTranslation()
  const cardsCollection = useRxCollection<Card>('cards')
  const userId = useAccountStore(state => state.id)
  const sourceLanguage = useSettingsStore(state => state.sourceLanguage.value)
  const learnLanguage = useSettingsStore(state => state.learnLanguage.value)
  const cardsFront = useSettingsStore(state => state.cardsFront)
  const isInitialized = useCardsGameStore(state => state.isInitialized)

  useEffect(() => {
    if (!isInitialized || !cardsCollection || !userId || !sourceLanguage || !learnLanguage) return

    const collectionSubscription = cardsCollection
      // find subscription is used instead of insert subscription, because it's a route specific hook
      .find({
        selector: {
          learnLanguage,
          sourceLanguage,
          userId,
          ...getPlayableCardsSnoozeSelector(cardsFront),
        },
      })
      .$.subscribe(allNonSnoozedCards => {
        const { shuffledCardIds, activeItemIndex, allCards } = useCardsGameStore.getState()
        // Only show toast if the user already started the game
        // The cards-game-bridge.tsx has the logic to reset the game if the user didn't start the game yet
        const isFirstCard = shuffledCardIds.length === activeItemIndex + 1
        const isUnknownStack = shuffledCardIds.length !== allCards.length
        if (isFirstCard && !isUnknownStack) return
        const shuffledCardsIdsSet = new Set(shuffledCardIds)

        const allNonSnoozedCardsIds = allNonSnoozedCards.map(card => card.id)
        const prevAllNonSnoozedCardsIds = usePrevAllNonSnoozedCardsIds.getState().value
        if (isSubset(prevAllNonSnoozedCardsIds, allNonSnoozedCardsIds)) return
        usePrevAllNonSnoozedCardsIds.setState({ value: allNonSnoozedCardsIds })

        const isNewCards = allNonSnoozedCardsIds.some(
          id => !shuffledCardsIdsSet.has(id) && id !== useLastRevertedCardId.getState().value,
        )
        if (isNewCards) toast.info(t('More cards are available. Refresh the stack to see them.'))
      })

    return () => {
      collectionSubscription.unsubscribe()
    }
  }, [isInitialized, cardsCollection, userId, sourceLanguage, learnLanguage, cardsFront, t])
}
